import { Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { Inbox01Icon } from '@/components/icons/Inbox01Icon';
import { Card } from '@/components/layout/Card/Card';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { COLORS } from '@/styles/tokens/colors';
import { RoleKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import {
  AdminRolesPage_RoleFragment,
  Operations,
  useUpdateRoleMutation,
} from '../graphql/GetRoles.generated';
import { DeleteRoleConfirmationModal } from './DeleteRoleConfirmationModal';
import { RoleForm } from './RoleForm';
import { RoleFormData } from './RoleViewer.types';
import { getRoleFormValues, getUpdateRoleInput } from './RoleViewer.utils';

export interface RoleViewerProps {
  role: AdminRolesPage_RoleFragment | undefined;
  allCapabilities: Array<{ id: string; authzedRelationName: string }>;
  onRoleDeleted?: () => void;
}

export function RoleViewer({
  role,
  allCapabilities,
  onRoleDeleted,
}: RoleViewerProps) {
  const formMethods = useForm<RoleFormData>({
    defaultValues: getRoleFormValues(role),
  });
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();

  const [updateRoleMutation] = useUpdateRoleMutation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { handleSubmit, formState, reset } = formMethods;

  const isAdministratorRole = role?.kind === RoleKind.Administrator;

  // Reset form when role changes
  useEffect(() => {
    reset(getRoleFormValues(role));
  }, [role, reset]);

  const onSave = useCallback(
    async (data: RoleFormData) => {
      if (!role) return;

      try {
        const input = getUpdateRoleInput(role.id, data, role, allCapabilities);

        await updateRoleMutation({
          variables: { input },
          refetchQueries: [Operations.Query.GetRoles],
        });

        trackUserEvent('role updated', {
          roleId: role.id,
          canApproveClientPresentations:
            data.generalProperties.canApproveClientPresentations,
        });
        showFeedback(FeedbackMessages.formSaveSuccess, { variant: 'success' });
      } catch (error) {
        diagnostics.error('Failed to update role:', error as Error);
        showFeedback(FeedbackMessages.formSaveError, { variant: 'error' });
      }
    },
    [role, allCapabilities, updateRoleMutation, showFeedback, trackUserEvent]
  );

  const onCancel = useCallback(() => {
    formMethods.reset();
  }, [formMethods]);

  const handleDeleteClick = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  if (!role) {
    return (
      <Card variant="outlined" sx={{ height: '100%', p: 2 }}>
        <Stack
          spacing={1}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Inbox01Icon size={30} color={COLORS.GRAY[300]} />
          <Typography variant="title-s" component="span">
            No role selected
          </Typography>
        </Stack>
      </Card>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <FormFieldsDisabledProvider
        isDisabled={formState.isSubmitting || isAdministratorRole}
      >
        <Card
          variant="outlined"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Scrollable Content */}
          <Stack sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <RoleForm
              variant="edit"
              onDeleteRole={isAdministratorRole ? undefined : handleDeleteClick}
            />
          </Stack>

          {/* Fixed Footer with Action Buttons */}
          {!isAdministratorRole && (
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              sx={{
                p: 1,
                borderTop: `1px solid ${COLORS.GRAY[200]}`,
                backgroundColor: 'white',
              }}
            >
              <Button
                variant="transparent"
                disabled={formState.isSubmitting}
                size="sm"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmit(onSave)}
                loading={formState.isSubmitting}
              >
                Save
              </Button>
            </Stack>
          )}
        </Card>
      </FormFieldsDisabledProvider>

      {/* Delete Confirmation Modal */}
      <DeleteRoleConfirmationModal
        isOpen={isDeleteModalOpen}
        role={role}
        onClose={handleDeleteModalClose}
        onDeleted={onRoleDeleted}
      />
    </FormProvider>
  );
}
